import React, { useState, useEffect } from 'react';
import { getFinancialSummary, getProjects, getExternalCosts } from '../lib/api';
import { formatCurrency, getMonthName } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { TrendingUp, TrendingDown, DollarSign, Receipt, PiggyBank, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const Revenue = () => {
  const [financialData, setFinancialData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [externalCosts, setExternalCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [viewMode, setViewMode] = useState('chart');

  const months = [
    { value: 'all', label: 'Celý rok' },
    { value: '1', label: 'Leden' },
    { value: '2', label: 'Únor' },
    { value: '3', label: 'Březen' },
    { value: '4', label: 'Duben' },
    { value: '5', label: 'Květen' },
    { value: '6', label: 'Červen' },
    { value: '7', label: 'Červenec' },
    { value: '8', label: 'Srpen' },
    { value: '9', label: 'Září' },
    { value: '10', label: 'Říjen' },
    { value: '11', label: 'Listopad' },
    { value: '12', label: 'Prosinec' },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { year: selectedYear };
      if (selectedMonth !== 'all') params.month = parseInt(selectedMonth);
      const [financialRes, projectsRes, extCostsRes] = await Promise.all([
        getFinancialSummary(params),
        getProjects({}),
        getExternalCosts(),
      ]);
      setFinancialData(financialRes.data);
      setProjects(projectsRes.data);
      setExternalCosts(extCostsRes.data || []);
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth]);

  // ── Chart data ──────────────────────────────────────────────
  const chartData = financialData.map((item) => ({
    name: getMonthName(item.month),
    month: item.month,
    'Odhadované tržby': item.estimated_revenue,
    'Reálné tržby': item.actual_revenue,
    'Náklady': item.costs,
    'Zisk': item.profit,
  }));

  const totals = financialData.reduce(
    (acc, item) => ({
      estimated: acc.estimated + item.estimated_revenue,
      actual: acc.actual + item.actual_revenue,
      costs: acc.costs + item.costs,
      profit: acc.profit + item.profit,
    }),
    { estimated: 0, actual: 0, costs: 0, profit: 0 }
  );

  const revenueAccuracy = totals.estimated > 0
    ? ((totals.actual / totals.estimated) * 100).toFixed(1)
    : 0;
  const profitMargin = totals.actual > 0
    ? ((totals.profit / totals.actual) * 100).toFixed(1)
    : 0;

  // ── Projects table ──────────────────────────────────────────
  const selectedMonthStr = selectedMonth !== 'all'
    ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
    : null;

  const projectsWithFinancials = projects.filter((p) => {
    const hasFinancials = (p.estimated_revenue || 0) > 0 || (p.actual_revenue || 0) > 0 || (p.total_cost || 0) > 0;
    if (!hasFinancials) return false;
    if (!p.end_date) return false;
    if (selectedMonthStr) {
      return p.end_date.startsWith(selectedMonthStr);
    }
    return p.end_date.startsWith(String(selectedYear));
  });

  // ── External costs for the period ──────────────────────────
  const calcExternalCostsForPeriod = () => {
    let total = 0;
    if (selectedMonthStr) {
      externalCosts.forEach((c) => {
        if (c.start_month <= selectedMonthStr && selectedMonthStr <= c.end_month) {
          total += c.amount;
        }
      });
    } else {
      for (let m = 1; m <= 12; m++) {
        const ms = `${selectedYear}-${String(m).padStart(2, '0')}`;
        externalCosts.forEach((c) => {
          if (c.start_month <= ms && ms <= c.end_month) total += c.amount;
        });
      }
    }
    return total;
  };
  const periodExternalCosts = calcExternalCostsForPeriod();

  // ── Project sums ────────────────────────────────────────────
  const projectSums = projectsWithFinancials.reduce(
    (acc, p) => {
      const est = p.estimated_revenue || 0;
      const actual = p.actual_revenue || 0;
      const cost = p.total_cost || 0;
      const profit = actual > 0 ? actual - cost : est - cost;
      return {
        estimated: acc.estimated + est,
        actual: acc.actual + actual,
        costs: acc.costs + cost,
        profit: acc.profit + profit,
      };
    },
    { estimated: 0, actual: 0, costs: 0, profit: 0 }
  );

  const totalCostsWithExt = projectSums.costs + periodExternalCosts;
  const revenueBase = projectSums.actual > 0 ? projectSums.actual : projectSums.estimated;
  const totalProfitWithExt = revenueBase - totalCostsWithExt;
  const totalMargin = revenueBase > 0 ? ((totalProfitWithExt / revenueBase) * 100).toFixed(1) : 0;

  const periodLabel = selectedMonthStr
    ? months.find((m) => m.value === selectedMonth)?.label + ` ${selectedYear}`
    : `Rok ${selectedYear}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revenue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Finanční přehled</h1>
          <p className="font-serif text-gray-600 mt-1">Odhadované vs. reálné tržby vs. náklady</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 border-2 border-gray-200 rounded-none" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-36 border-2 border-gray-200 rounded-none" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border-2 border-gray-200">
            <button
              onClick={() => setViewMode('chart')}
              className={`px-4 py-2 font-oswald uppercase text-sm ${viewMode === 'chart' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'}`}
            >
              Graf
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 font-oswald uppercase text-sm ${viewMode === 'table' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'}`}
            >
              Tabulka
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#006CB6] text-white"><TrendingUp size={20} /></div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Odhadované tržby</div>
          </div>
          <div className="font-oswald text-3xl font-bold" data-testid="total-estimated">{formatCurrency(totals.estimated)}</div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#F4E600] text-black"><DollarSign size={20} /></div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Reálné tržby</div>
          </div>
          <div className="font-oswald text-3xl font-bold" data-testid="total-actual">{formatCurrency(totals.actual)}</div>
          <div className="text-sm font-serif text-gray-500 mt-1">{revenueAccuracy}% odhadu</div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-500 text-white"><Receipt size={20} /></div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Náklady</div>
          </div>
          <div className="font-oswald text-3xl font-bold text-red-600" data-testid="total-costs">{formatCurrency(totals.costs)}</div>
          <div className="text-sm font-serif text-gray-500 mt-1">Čas × hodinová sazba</div>
        </div>
        <div className={`bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] ${totals.profit >= 0 ? '' : 'bg-red-50'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 ${totals.profit >= 0 ? 'bg-green-500' : 'bg-red-500'} text-white`}><PiggyBank size={20} /></div>
            <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Zisk</div>
          </div>
          <div className={`font-oswald text-3xl font-bold flex items-center gap-2 ${totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="total-profit">
            {totals.profit >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
            {formatCurrency(totals.profit)}
          </div>
          <div className="text-sm font-serif text-gray-500 mt-1">Marže: {profitMargin}%</div>
        </div>
      </div>

      {/* Chart */}
      {viewMode === 'chart' && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">Porovnání {selectedYear}</h2>
          {chartData.length > 0 ? (
            <div className="h-96" data-testid="financial-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontFamily: 'Oswald', fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontFamily: 'Source Serif 4', fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ fontFamily: 'Source Serif 4', border: '2px solid black', borderRadius: 0 }} />
                  <Legend wrapperStyle={{ fontFamily: 'Oswald', textTransform: 'uppercase', fontSize: '12px' }} />
                  <Bar dataKey="Odhadované tržby" fill="#006CB6" />
                  <Bar dataKey="Reálné tržby" fill="#F4E600" />
                  <Bar dataKey="Náklady" fill="#ef4444" />
                  <Line type="monotone" dataKey="Zisk" stroke="#22c55e" strokeWidth={3} dot={{ fill: '#22c55e', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="font-serif text-gray-500">Žádná finanční data pro rok {selectedYear}</p>
            </div>
          )}
        </div>
      )}

      {/* Monthly table */}
      {viewMode === 'table' && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">Měsíční přehled {selectedYear}</h2>
          {chartData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="financial-table">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Měsíc</th>
                    <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Odhad</th>
                    <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Realita</th>
                    <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Náklady</th>
                    <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Zisk</th>
                    <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Marže</th>
                  </tr>
                </thead>
                <tbody>
                  {financialData.map((item) => {
                    const margin = item.actual_revenue > 0 ? ((item.profit / item.actual_revenue) * 100).toFixed(1) : 0;
                    return (
                      <tr key={`${item.year}-${item.month}`} className="border-b border-gray-200">
                        <td className="py-3 font-serif">{getMonthName(item.month)}</td>
                        <td className="py-3 text-right font-serif">{formatCurrency(item.estimated_revenue)}</td>
                        <td className="py-3 text-right font-serif">{formatCurrency(item.actual_revenue)}</td>
                        <td className="py-3 text-right font-serif text-red-600">{formatCurrency(item.costs)}</td>
                        <td className={`py-3 text-right font-serif font-bold ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {item.profit >= 0 ? '+' : ''}{formatCurrency(item.profit)}
                        </td>
                        <td className={`py-3 text-right font-serif ${parseFloat(margin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{margin}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-black bg-gray-50">
                    <td className="py-3 font-oswald uppercase font-bold">Celkem</td>
                    <td className="py-3 text-right font-oswald font-bold">{formatCurrency(totals.estimated)}</td>
                    <td className="py-3 text-right font-oswald font-bold">{formatCurrency(totals.actual)}</td>
                    <td className="py-3 text-right font-oswald font-bold text-red-600">{formatCurrency(totals.costs)}</td>
                    <td className={`py-3 text-right font-oswald font-bold ${totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totals.profit >= 0 ? '+' : ''}{formatCurrency(totals.profit)}
                    </td>
                    <td className={`py-3 text-right font-oswald font-bold ${parseFloat(profitMargin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{profitMargin}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="font-serif text-gray-500">Žádná finanční data pro rok {selectedYear}</p>
            </div>
          )}
        </div>
      )}

      {/* Projects financial table — filtered by end_date month */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight">
            Projekty — {periodLabel}
          </h2>
          <span className="font-serif text-sm text-gray-500">
            Filtrováno dle data ukončení projektu
          </span>
        </div>

        {projectsWithFinancials.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="projects-financial-table">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3 pr-4">Projekt</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3 pr-4">Klient</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3 pr-4">Status</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3 pr-4">Odh. tržby</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3 pr-4">Náklady</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3 pr-4">Reálné tržby</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3 pr-4">Zisk</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Marže</th>
                </tr>
              </thead>
              <tbody>
                {projectsWithFinancials.map((project) => {
                  const est = project.estimated_revenue || 0;
                  const actual = project.actual_revenue || 0;
                  const cost = project.total_cost || 0;
                  const revenueForCalc = actual > 0 ? actual : est;
                  const profit = revenueForCalc - cost;
                  const margin = revenueForCalc > 0 ? ((profit / revenueForCalc) * 100).toFixed(1) : null;
                  const isInvoiced = project.status === 'invoiced';

                  return (
                    <tr key={project.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4 font-serif font-medium">{project.name}</td>
                      <td className="py-3 pr-4 font-serif text-gray-500 text-sm">{project.client_name}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-0.5 text-xs font-oswald uppercase border ${
                          isInvoiced ? 'bg-[#006CB6] text-white border-[#006CB6]' :
                          project.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-300' :
                          project.status === 'active' ? 'bg-green-50 text-green-700 border-green-300' :
                          'bg-gray-100 text-gray-600 border-gray-300'
                        }`}>
                          {isInvoiced ? 'Fakturováno' :
                           project.status === 'completed' ? 'Dokončeno' :
                           project.status === 'active' ? 'Aktivní' : project.status}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right font-serif text-sm">
                        {est > 0 ? formatCurrency(est) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 pr-4 text-right font-serif text-sm text-red-600">
                        {cost > 0 ? formatCurrency(cost) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 pr-4 text-right font-serif text-sm">
                        {actual > 0
                          ? <span className="font-medium">{formatCurrency(actual)}</span>
                          : <span className="text-gray-400 italic text-xs">nefakturováno</span>}
                      </td>
                      <td className={`py-3 pr-4 text-right font-serif font-medium text-sm ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {revenueForCalc > 0
                          ? <>{profit >= 0 ? '+' : ''}{formatCurrency(profit)}{actual === 0 && <span className="text-gray-400 text-xs ml-1">(odhad)</span>}</>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className={`py-3 text-right font-serif text-sm ${margin !== null && parseFloat(margin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {margin !== null ? `${margin}%` : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {/* Project totals row */}
                <tr className="border-t-2 border-black bg-gray-50">
                  <td colSpan={3} className="py-3 pr-4 font-oswald uppercase font-bold text-sm">Suma projektů</td>
                  <td className="py-3 pr-4 text-right font-oswald font-bold text-sm">{formatCurrency(projectSums.estimated)}</td>
                  <td className="py-3 pr-4 text-right font-oswald font-bold text-sm text-red-600">{formatCurrency(projectSums.costs)}</td>
                  <td className="py-3 pr-4 text-right font-oswald font-bold text-sm">{formatCurrency(projectSums.actual)}</td>
                  <td className={`py-3 pr-4 text-right font-oswald font-bold text-sm ${projectSums.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {projectSums.profit >= 0 ? '+' : ''}{formatCurrency(projectSums.profit)}
                  </td>
                  <td className="py-3 text-right font-oswald font-bold text-sm text-gray-400">—</td>
                </tr>

                {/* External costs row */}
                <tr className="border-t border-gray-300 bg-red-50">
                  <td colSpan={3} className="py-2 pr-4 font-serif text-sm text-gray-600 flex items-center gap-1">
                    <span className="font-oswald uppercase text-xs">+ Ext. náklady</span>
                    <span className="text-xs text-gray-400">({periodLabel})</span>
                  </td>
                  <td className="py-2 pr-4 text-right font-serif text-sm text-gray-400">—</td>
                  <td className="py-2 pr-4 text-right font-serif text-sm text-red-600 font-medium">
                    {periodExternalCosts > 0 ? formatCurrency(periodExternalCosts) : <span className="text-gray-400">0 Kč</span>}
                  </td>
                  <td className="py-2 pr-4 text-right font-serif text-sm text-gray-400">—</td>
                  <td className="py-2 pr-4 text-right font-serif text-sm text-red-600 font-medium">
                    {periodExternalCosts > 0 ? `−${formatCurrency(periodExternalCosts)}` : '—'}
                  </td>
                  <td className="py-2 text-right font-serif text-sm text-gray-400">—</td>
                </tr>

                {/* Final total row */}
                <tr className="border-t-2 border-black bg-gray-100">
                  <td colSpan={3} className="py-3 pr-4 font-oswald uppercase font-bold">Celkový výsledek</td>
                  <td className="py-3 pr-4 text-right font-oswald font-bold">{formatCurrency(projectSums.estimated)}</td>
                  <td className="py-3 pr-4 text-right font-oswald font-bold text-red-600">{formatCurrency(totalCostsWithExt)}</td>
                  <td className="py-3 pr-4 text-right font-oswald font-bold">{formatCurrency(projectSums.actual)}</td>
                  <td className={`py-3 pr-4 text-right font-oswald font-bold ${totalProfitWithExt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalProfitWithExt >= 0 ? '+' : ''}{formatCurrency(totalProfitWithExt)}
                  </td>
                  <td className={`py-3 text-right font-oswald font-bold ${parseFloat(totalMargin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalMargin}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="font-serif text-gray-500">
              Žádné projekty s finančními údaji pro {periodLabel}
            </p>
            <p className="font-serif text-gray-400 text-sm mt-1">
              Projekty jsou filtrovány dle data ukončení (end_date)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Revenue;